import type { ChosenImage } from './PlaceImageEditor';

function imageKind(mime: string): 'png' | 'jpeg' | null {
  if (mime === 'image/png') return 'png';
  return mime === 'image/jpeg' ? 'jpeg' : null;
}

/** Reads a PNG or JPEG chosen for placement; null for anything else. */
export async function readImage(file: File): Promise<ChosenImage | null> {
  const type = imageKind(file.type);
  if (!type) return null;
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;
  try {
    await image.decode();
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
  // The browser shows a phone photo upright (EXIF orientation); the PDF would embed the raw, turned
  // pixels. A JPEG is therefore redrawn as it is shown; a PNG has no orientation and keeps its bytes
  // (and its transparency) as they are.
  const bytes = type === 'png' ? await file.arrayBuffer() : await uprightJpeg(image);
  return {
    name: file.name,
    bytes,
    type,
    url,
    naturalWidth: image.naturalWidth,
    naturalHeight: image.naturalHeight,
  };
}

async function uprightJpeg(image: HTMLImageElement): Promise<ArrayBuffer> {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  canvas.getContext('2d')?.drawImage(image, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92),
  );
  canvas.width = 0;
  canvas.height = 0;
  if (!blob) throw new Error('Could not encode the image');
  return blob.arrayBuffer();
}
